import { AlertType, IAlert } from './interfaces/IAlert';
import { IUser } from './interfaces/IUser';
import { IRestaurant } from './interfaces/IRestaurant';
import { Globals } from './modules/Globals';
import { IBranch } from './interfaces/IBranch';
import { ICurrency } from './interfaces/ICurrency';
import { IMember, MemberRole } from './interfaces/IMember';
import { PermissionType } from './enums/PermissionType';
import { PrintPluginStatus } from './modules/PrintService';
import { TicketPrintSetting } from './enums/PrintEnum';
import { PrintFormat } from './lib/print/EscPosEncoder';
import ConfirmModal from './components/modals/ConfirmModal.vue';
import { ILanguage } from './interfaces/ILanguage';
import ILocationData from './interfaces/ILocationData';
import { IRestaurantSettings } from './api/services/restaurants.service';
import BranchesService, {
  defaultTicketSettings,
} from './api/services/branches.service';
import ITicketSettings from './interfaces/ITicketSettings';
import { ICategory } from './interfaces/ICategory';
import { IItemSearchResumed } from './interfaces/IItemSearchResumed';
import { ICategorySearchResumed } from './interfaces/ICategorySearchResumed';
import CategoriesService from './api/services/categories.service';
import ItemsService from './api/services/items.service';
import { IItem } from './interfaces/IItem';
import InputModal from './components/modals/InputModal.vue';
import SelectModal from './components/modals/SelectModal.vue';
import { useRestaurantStore } from './stores/restaurant.store';
import { useSocket } from './composition/use-socket';
import { useAuth, useAuthStore } from './composition/use-auth';
import logdna from '@logdna/browser';
import { getServerErrorMessage } from './utils/error.utils';
import { recordErrorHistoryEntry } from './lib/support/error-history';
import { setSentryUser, setSentryRestaurant } from './lib/sentry';

/** Ids of the toasts (`IAlert.id`): stable keys for the toast stack. */
let alertSequence = 0;

class VueData {
  alerts: IAlert[] = [];
  user: IUser = null;
  restaurant: IRestaurant = null;
  restaurantLanguages: ILanguage[] = null;
  restaurantSettings: IRestaurantSettings = null;
  currency: ICurrency = null;
  role: IMember = null;
  permissions: number = null;
  branches: IBranch[] = null;
  allBranches: IBranch[] = null;
  branch: IBranch = null;
  noNotifyOrderIds: number[] = [];
  windowWidth = window.innerWidth;
  ticketSettings: ITicketSettings = { ...defaultTicketSettings };
  version = Globals.version;
  latestVersion = Globals.version;
  locationData: ILocationData = null;
  country: string = null;
  categoriesCache: Map<number | null, ICategorySearchResumed[]> = null;
  //categoriesCache: Map<number | null, ICategorySearchResumed[]> = null;
  itemsCache: Map<number | null, IItemSearchResumed[]> = null;
  /** Written by useWhatsNew; read by the drawer's Club-row «Nuevo» chip (app-nav). */
  clubLaunchChip = false;
  restaurantStore: ReturnType<typeof useRestaurantStore>;
  socketHelper: ReturnType<typeof useSocket>;
  authHelper: ReturnType<typeof useAuth>;
  authStore: ReturnType<typeof useAuthStore>;
  logdnaInitialized = false;

  // App initialization state
  isInitializing = true;
  connectionError: Error | null = null;
  initializationError: Error | null = null;

  init() {
    this.restaurantStore = useRestaurantStore();
    this.authStore = useAuthStore();
    this.authHelper = useAuth();
    this.socketHelper = useSocket();
    this.fillStores();
    this.initLogDna();
  }

  fillStores() {
    this.authHelper.setUser(this.user);
    this.authStore.setPermissions(this.permissions);
  }

  initLogDna() {
    if (this.logdnaInitialized) return;
    try {
      logdna.init('c03da8c6fb9680413a17eb4c4080d989', {
        app: 'queresto-admin',
        console: {
          enable: true,
          integrations: ['error', 'warn'],
        },
      });
      logdna.addContext({
        environment: Globals.IsLocalhost ? 'local' : 'production',
        version: Globals.version,
      });
      if (this.restaurant) {
        logdna.addContext({
          restaurantId: this.restaurant.id,
          restaurantName: this.restaurant.name,
        });
      }
      this.logdnaInitialized = true;
    } catch (e) {
      console.error('Error initializing logdna', e);
    }
  }

  setInitializing(value: boolean) {
    this.isInitializing = value;
  }

  setConnectionError(error: Error | null) {
    this.connectionError = error;
  }

  setInitializationError(error: Error | null) {
    this.initializationError = error;
  }

  addAlert(
    message: string,
    type: AlertType,
    time: number = 3000,
    action?: { text: string; cb: () => void }
  ): IAlert {
    const alert: IAlert = { id: ++alertSequence, message, type, action };

    this.alerts.push(alert);

    setTimeout(() => {
      this.removeAlert(alert);
    }, time);
    return alert;
  }

  removeAlert(alert: IAlert) {
    const index = this.alerts.indexOf(alert);
    if (index > -1) this.alerts.splice(index, 1);
  }

  setUser(user: IUser) {
    this.user = user;
    this.authHelper?.setUser?.(user);
    setSentryUser(user ?? null);
  }

  setCatalogCache(
    categories: Map<number | null, ICategorySearchResumed[]>,
    items: Map<number | null, IItemSearchResumed[]>
  ) {
    this.categoriesCache = categories;
    this.itemsCache = items;
  }

  async reloadCatalogCache() {
    const itemsService = new ItemsService();
    const categoriesService = new CategoriesService();
    let itemsMap, categoriesMap;
    const loadItems = async () => {
      const items = await ItemsService.getAllFull({
        restaurantId: this.restaurant.id,
      });
      itemsMap = new Map<number | null, IItem[]>();

      for (const item of items) {
        let group: IItem[] = itemsMap.get(item.menuId);
        if (!group) itemsMap.set(item.menuId, (group = []));

        group.push(item);
      }
    };
    const loadCategories = async () => {
      const categories = await categoriesService.searchResumed(
        this.restaurant.id,
        this.branch.id,
        null,
        'all'
      );
      categoriesMap = new Map<number | null, ICategorySearchResumed[]>();

      for (const cat of categories) {
        let group: ICategorySearchResumed[] = categoriesMap.get(cat.menuId);
        if (!group) categoriesMap.set(cat.menuId, (group = []));

        group.push(cat);
      }
    };

    await Promise.all([loadItems(), loadCategories()]);

    this.setCatalogCache(categoriesMap, itemsMap);
  }

  async getOrLoadCatalogCache() {
    if (!this.categoriesCache || !this.itemsCache) {
      await this.reloadCatalogCache();
    }
    return { categories: this.categoriesCache, items: this.itemsCache };
  }

  setTicketSettings(ticketSettings: ITicketSettings) {
    this.ticketSettings = { ...ticketSettings };
  }

  setCurrentRestaurant(restaurant: IRestaurant) {
    this.restaurant = restaurant;
    this.restaurantStore.setRestaurant(restaurant);
    this.updateLanguages();
    setSentryRestaurant(restaurant ?? null);
    if (this.logdnaInitialized && restaurant) {
      logdna.addContext({
        restaurantId: restaurant.id,
        restaurantName: restaurant.name,
      });
    }
  }

  setCurrentRestaurantSettings(settings: IRestaurantSettings) {
    this.restaurantSettings = settings;
  }

  updateLanguages() {
    if (this.restaurant && this.restaurant.languageIds) {
      this.restaurantLanguages = Globals.languages.filter(
        (l) => this.restaurant.languageIds.indexOf(l.id) > -1
      );
    } else {
      this.restaurantLanguages = null;
    }
  }

  setCurrentRole(role: IMember) {
    this.role = role;
    this.authStore?.setCurrentRole?.(role);

    if (!this.role) {
      this.permissions = null;
      this.authStore?.setPermissions?.(null);
    } else {
      this.permissions = this.role.permissions;
      this.authStore?.setPermissions?.(this.permissions);
    }
  }

  hasPermission(perm: PermissionType) {
    if (!this.role) return false;
    // tslint:disable-next-line:no-bitwise
    else
      return (
        this.role.type === MemberRole.Owner ||
        this.role.type === MemberRole.Admin ||
        (this.permissions & perm) === perm
      );
  }

  setCurrency(currency: ICurrency) {
    this.currency = currency;
    this.restaurantStore.setCurrency(currency);
  }

  setBranches(branchesWithRole: IBranch[], allBranches?: IBranch[]) {
    this.branches = branchesWithRole;
    this.allBranches =
      typeof allBranches === 'undefined' ? branchesWithRole : allBranches;
    this.restaurantStore.setBranches(this.branches);
  }

  setCurrentBranch(branch: IBranch) {
    this.branch = branch;
    this.restaurantStore.setSelectedBranch(branch);

    if (branch) {
      if (Globals.hasLocalStorage() && this.branches.length > 1)
        localStorage.setItem(`${this.restaurant.id}-branch`, branch.id + '');
      this.loadTicketSettings();
    }
  }

  async loadTicketSettings() {
    const service = new BranchesService();
    this.setTicketSettings(await service.getTicketSettings(this.branch.id));
  }

  serverError(error, defaultMessage: string = 'Error, intente nuevamente') {
    console.error(error);

    const errorMessage = getServerErrorMessage(error, {
      defaultMessage,
    });

    this.logException(error, 'Server error', true, errorMessage);
    this.addAlert(errorMessage, AlertType.Error);
  }

  conversion(id: string, label: string, name: string) {
    // @ts-ignore
    if (window.gtag) {
      try {
        // @ts-ignore
        window.gtag('event', 'conversion', {
          send_to: `AW-${id}/${label}`,
        });
      } catch (e) {
        console.error('Error en conversion', e);
      }
    }
  }

  /**
   * Logs exception to gtag with a more descriptive error message.
   */
  logException(
    e: unknown,
    source: string = 'Unknown',
    logToDna: boolean = true,
    errorMessagePrefix: string = ''
  ) {
    // Support-report ring buffer: independent from gtag availability.
    recordErrorHistoryEntry(
      errorMessagePrefix ? `${source}: ${errorMessagePrefix}` : source,
      e
    );

    // @ts-ignore
    if (window.gtag) {
      try {
        const restaurantId = this.restaurant?.id;
        let description = `${source} Exception: ${
          errorMessagePrefix ? errorMessagePrefix + ' - ' : ''
        }`;

        if (restaurantId) {
          description += `Restaurant ID: ${restaurantId} - `;
        }

        if (e instanceof Error) {
          // Standard Error object - has message and stack
          description += `Message: ${e.message} - Stack: ${e.stack}`;
        } else if (typeof e === 'string') {
          //  Handle string errors
          description += `String error: ${e}`;
        } else {
          // Handle other types of exceptions
          description += `Unknown error type: ${JSON.stringify(e)}`;
        }

        // @ts-ignore
        window.gtag('event', 'exception', {
          description: description,
          fatal: false, // You might want to set this to true for *some* exceptions
        });

        if (this.logdnaInitialized && logToDna) logdna.error(description);
      } catch (innerError) {
        console.error('Error in logException', innerError);
      }
    }
  }

  showDynamicDialog<T>(modalInstance, component, props, name = 'modal') {
    return new Promise<T>((resolve) => {
      // @ts-ignore
      modalInstance.show(
        component,
        props,
        {
          name,
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          width: 600,
        },
        {
          submit: async (value: any) => {
            modalInstance.hide(name);
            resolve(value);
          },
          closed: async () => {
            resolve(null);
          },
        }
      );
    });
  }

  showConfirmDialog(
    modalInstance,
    title: string,
    message: string,
    acceptText?: string,
    cancelText?: string,
    name = 'modal'
  ) {
    return new Promise<boolean>((resolve) => {
      // @ts-ignore
      modalInstance.show(
        ConfirmModal,
        {
          title,
          message,
          acceptText,
          cancelText,
        },
        {
          name,
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          width: 600,
        },
        {
          confirm: async () => {
            modalInstance.hide(name);
            resolve(true);
          },
          closed: async () => {
            resolve(false);
          },
        }
      );
    });
  }

  showInputDialog({
    modalInstance,
    title,
    value,
    type,
    message,
    hint,
    placeholder,
    prefix,
    rules,
  }: {
    modalInstance;
    title: string;
    value: any;
    type: string;
    message: string;
    hint: string;
    placeholder: string;
    prefix: string;
    rules: any[];
  }) {
    return new Promise<string>((resolve) => {
      // @ts-ignore
      modalInstance.show(
        InputModal,
        {
          title,
          value,
          type,
          message,
          hint,
          placeholder,
          prefix,
          rules,
        },
        {
          name: 'modal',
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          width: 600,
        },
        {
          submit: async (value: string) => {
            modalInstance.hide('modal');
            resolve(value);
          },
          closed: async () => {
            resolve(null);
          },
        }
      );
    });
  }

  showSelectDialog({
    modalInstance,
    title,
    message,
    placeholder,
    items,
  }: {
    modalInstance;
    title: string;
    message: string;
    placeholder: string;
    items: { value: string | number; text: string }[];
  }) {
    return new Promise<string>((resolve) => {
      // @ts-ignore
      modalInstance.show(
        SelectModal,
        {
          title,
          message,
          placeholder,
          items,
        },
        {
          name: 'modal',
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          width: 600,
        },
        {
          submit: async (value: string) => {
            modalInstance.hide('modal');
            resolve(value);
          },
          closed: async () => {
            resolve(null);
          },
        }
      );
    });
  }
}

// Catch uncaught errors and reject promises and log them to gtag
/* window.onerror = (message, source, lineno, colno, error) => {
  console.error('Global onerror', { message, source, lineno, colno, error });
  vueData.logException(error || message, 'Global onerror');
}; */
window.addEventListener('error', (event) => {
  console.error('Global onerror', event);
  vueData.logException(event.error || event.message, 'Global onerror', false);
  event.preventDefault();
});
window.addEventListener('unhandledrejection', (event) => {
  console.error('Global unhandledrejection', event);
  vueData.logException(event.reason, 'Global unhandledrejection', false);
  if (!Globals.IsLocalhost) event.preventDefault();
});

const vueData = new VueData();

export { vueData };
